import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Alert,
  Chip,
  Collapse,
  InputAdornment,
  Avatar,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Skeleton,
  Paper,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import api, { enhancedApi } from './api';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import PageLayout from './components/ui/PageLayout';
import ClientForm from './components/ClientForm';

const STATUS_FILTERS = [
  { value: 'All', label: 'All' },
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
  { value: 'Lead', label: 'Lead' },
];

const TYPE_FILTERS = [
  { value: 'All', label: 'All' },
  { value: 'Individual', label: 'Individual' },
  { value: 'Business', label: 'Business' },
  { value: 'Trust', label: 'Trust' },
  { value: 'Estate', label: 'Estate' },
];

const getInitials = (name) => {
  if (!name || !String(name).trim()) return '?';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (name[0] || '?').toUpperCase();
};

const getHealthMeta = (score) => {
  if (score == null || score === '') return { label: '—', color: 'default' };
  const s = Number(score);
  if (Number.isNaN(s)) return { label: '—', color: 'default' };
  if (s >= 70) return { label: 'Good', color: 'success' };
  if (s >= 40) return { label: 'Fair', color: 'warning' };
  return { label: 'Poor', color: 'error' };
};

const formatRelativeActivity = (row) => {
  const val = row.updated_at ?? row.created_at ?? row.client_since;
  if (!val) return '—';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  const now = Date.now();
  const sec = Math.round((now - d.getTime()) / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} day${day === 1 ? '' : 's'} ago`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk} week${wk === 1 ? '' : 's'} ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo} month${mo === 1 ? '' : 's'} ago`;
  const yr = Math.floor(day / 365);
  return `${yr} year${yr === 1 ? '' : 's'} ago`;
};

const formatLastActivityCsv = (row) => {
  const val = row.updated_at ?? row.created_at ?? row.client_since;
  if (!val) return '';
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
};

function LoadingSkeletonOverlay() {
  return (
    <Box sx={{ width: '100%', py: 2, px: 2, boxSizing: 'border-box' }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} variant="rounded" height={48} sx={{ mb: 1, borderRadius: 1 }} />
      ))}
    </Box>
  );
}

function ClientList() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterClientType, setFilterClientType] = useState('All');
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [showClientForm, setShowClientForm] = useState(false);
  const [assignWorkflowOpen, setAssignWorkflowOpen] = useState(false);
  const [workflowTemplates, setWorkflowTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [assignWorkflowSending, setAssignWorkflowSending] = useState(false);
  const [assignWorkflowError, setAssignWorkflowError] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteSending, setDeleteSending] = useState(false);

  const handleOptionSelect = (option) => {
    switch (option) {
      case 'home':
        navigate('/dashboard');
        break;
      case 'folder':
        navigate('/files/clients/root');
        break;
      case 'settings':
        navigate('/settings');
        break;
      default:
        break;
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [clientsRes, healthScoresRes] = await Promise.all([
        enhancedApi.getClients(),
        enhancedApi.getHealthScores().catch(() => ({ data: { results: [] } })),
      ]);
      const data = clientsRes.data;
      const list = Array.isArray(data) ? data : (data?.data || []);
      const pc = (item) => item.primary_contact || {};
      const scoresList = healthScoresRes?.data?.results ?? healthScoresRes?.data?.data ?? [];
      const scoresMap = {};
      scoresList.forEach((s) => {
        const cid = s.client_id ?? s.id;
        if (cid != null) scoresMap[cid] = { score: s.score };
      });
      const flattenedData = list.map((item) => {
        const hs = scoresMap[item.id];
        const primary = pc(item);
        const members = item.team_members ?? (primary.assigned_users?.map((u) => u.username || u.email) || []);
        const tagsList = item.tags ?? primary.tags ?? [];
        const tagNames = Array.isArray(tagsList)
          ? tagsList.map((t) => (typeof t === 'object' ? t?.name : t)).filter(Boolean)
          : [];
        return {
          ...item,
          display_name: item.display_name || primary.display_name || `${primary.first_name || ''} ${primary.last_name || ''}`.trim() || 'Unknown',
          client_since: item.client_since ?? primary.client_since ?? '',
          client_type: item.client_type ?? primary.client_type ?? '',
          lifecycle_stage: item.lifecycle_stage ?? '',
          is_active: item.is_active !== undefined ? item.is_active : true,
          tags: tagsList,
          team_members: members,
          email: item.email ?? primary.emails?.[0]?.email ?? primary.email ?? '',
          phone: item.phone ?? primary.phones?.[0]?.phone ?? primary.phone ?? '',
          health_score: hs?.score,
          tagNames,
          updated_at: item.updated_at,
          created_at: item.created_at,
        };
      });
      setRows(flattenedData);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtersActive = filterStatus !== 'All' || filterClientType !== 'All';

  const filteredRows = useMemo(
    () =>
      rows.filter((row) => {
        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase();
          const matchSearch =
            (row.display_name || '').toLowerCase().includes(q) ||
            (row.first_name || '').toLowerCase().includes(q) ||
            (row.last_name || '').toLowerCase().includes(q) ||
            (row.email || '').toLowerCase().includes(q);
          if (!matchSearch) return false;
        }
        if (filterStatus === 'Active' && row.is_active === false) return false;
        if (filterStatus === 'Inactive' && row.is_active !== false) return false;
        if (filterStatus === 'Lead') {
          const stage = String(row.lifecycle_stage || '').toLowerCase();
          if (stage !== 'lead') return false;
        }
        if (filterClientType && filterClientType !== 'All') {
          const ct = (row.client_type || '').toLowerCase();
          const ft = filterClientType.toLowerCase();
          if (ct !== ft) return false;
        }
        return true;
      }),
    [rows, searchQuery, filterStatus, filterClientType]
  );

  const handleClearFilters = () => {
    setFilterStatus('All');
    setFilterClientType('All');
  };

  const handleNameClick = useCallback(
    async (id, row) => {
      try {
        await enhancedApi.addRecentClient(id);
        const response = await enhancedApi.getClient(id);
        navigate(`/client/${id}`, { state: { client: response.data } });
      } catch {
        navigate(`/client/${id}`, { state: { client: row } });
      }
    },
    [navigate]
  );

  const handleAssignWorkflow = async () => {
    if (!selectedTemplateId || rowSelectionModel.length === 0) return;
    setAssignWorkflowSending(true);
    setAssignWorkflowError('');
    try {
      for (const clientId of rowSelectionModel) {
        await api.post('/api/multi-tenant/assign-workflow/', {
          template_id: selectedTemplateId,
          client_id: clientId,
        });
      }
      setAssignWorkflowOpen(false);
      setSelectedTemplateId('');
      setRowSelectionModel([]);
      fetchData();
    } catch (err) {
      setAssignWorkflowError(err.response?.data?.error || 'Failed to assign workflow');
    } finally {
      setAssignWorkflowSending(false);
    }
  };

  const handleExport = () => {
    const selected = filteredRows.filter((r) => rowSelectionModel.includes(r.id));
    if (selected.length === 0) return;
    const headers = ['Name', 'Email', 'Phone', 'Type', 'Assigned Staff', 'Health Score', 'Last Activity'];
    const escapeCsv = (val) => {
      const s = String(val ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [
      headers.join(','),
      ...selected.map((r) =>
        [
          escapeCsv(r.display_name),
          escapeCsv(r.email),
          escapeCsv(r.phone),
          escapeCsv(r.client_type),
          escapeCsv(Array.isArray(r.team_members) ? r.team_members.join('; ') : r.team_members),
          escapeCsv(r.health_score ?? ''),
          escapeCsv(formatLastActivityCsv(r)),
        ].join(',')
      ),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clients-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDeleteSelected = async () => {
    if (rowSelectionModel.length === 0) return;
    setDeleteSending(true);
    try {
      for (const id of rowSelectionModel) {
        await enhancedApi.deleteClient(id);
      }
      setDeleteConfirmOpen(false);
      setRowSelectionModel([]);
      fetchData();
    } catch {
      // errors surfaced via UI elsewhere if needed
    } finally {
      setDeleteSending(false);
    }
  };

  useEffect(() => {
    if (assignWorkflowOpen) {
      setSelectedTemplateId('');
      setAssignWorkflowError('');
      enhancedApi
        .getWorkflowTemplates()
        .then((res) => {
          const data = res.data;
          const list = Array.isArray(data) ? data : (data?.results ?? data?.data ?? []);
          setWorkflowTemplates(list);
        })
        .catch(() => setWorkflowTemplates([]));
    }
  }, [assignWorkflowOpen]);

  const columns = useMemo(
    () => [
      {
        field: 'display_name',
        headerName: 'Client',
        flex: 1,
        minWidth: 240,
        renderCell: (params) => {
          const { id, row } = params;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'primary.main',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                }}
              >
                {getInitials(row.display_name)}
              </Avatar>
              <Link
                component={RouterLink}
                to={`/client/${id}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNameClick(id, row);
                }}
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {row.display_name || '—'}
              </Link>
            </Box>
          );
        },
      },
      { field: 'email', headerName: 'Email', flex: 0.8, minWidth: 180 },
      { field: 'phone', headerName: 'Phone', width: 140 },
      {
        field: 'client_type',
        headerName: 'Type',
        width: 120,
        renderCell: (params) => (
          <Chip
            label={params.value || '—'}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
        ),
      },
      {
        field: 'team_members',
        headerName: 'Assigned Staff',
        flex: 0.7,
        minWidth: 160,
        renderCell: (params) => {
          const members = Array.isArray(params.value) ? params.value : [];
          if (members.length === 0) return '—';
          if (members.length <= 2) return members.join(', ');
          return `${members.slice(0, 2).join(', ')} +${members.length - 2}`;
        },
      },
      {
        field: 'health_score',
        headerName: 'Health Score',
        width: 130,
        renderCell: (params) => {
          const meta = getHealthMeta(params.row.health_score);
          if (meta.label === '—') return '—';
          return (
            <Chip
              label={meta.label}
              color={meta.color}
              size="small"
              variant="filled"
              sx={{ fontWeight: 600 }}
            />
          );
        },
      },
      {
        field: 'last_activity',
        headerName: 'Last Activity',
        width: 130,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {formatRelativeActivity(params.row)}
          </Typography>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const { id, row } = params;
          return (
            <Box
              className="client-list-actions"
              sx={{
                display: 'flex',
                gap: 0.25,
                opacity: 0,
                transition: 'opacity 0.15s ease',
                '.MuiDataGrid-row:hover &': { opacity: 1 },
                '@media (hover: none)': { opacity: 1 },
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <IconButton size="small" onClick={() => handleNameClick(id, row)} aria-label="View">
                <VisibilityIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => handleNameClick(id, row)} aria-label="Edit">
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Delete"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (window.confirm('Delete this client?')) {
                    try {
                      await enhancedApi.deleteClient(id);
                      fetchData();
                    } catch {
                      // ignore
                    }
                  }
                }}
                sx={{ color: 'error.main' }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [handleNameClick]
  );

  const emptyPortfolio = !loading && rows.length === 0;
  const noFilterResults = !loading && rows.length > 0 && filteredRows.length === 0;
  const totalLabel =
    searchQuery.trim() || filtersActive
      ? `${rows.length} client${rows.length !== 1 ? 's' : ''} total · ${filteredRows.length} shown`
      : `${rows.length} client${rows.length !== 1 ? 's' : ''} total`;

  return (
    <PageLayout onOptionSelect={handleOptionSelect} sx={{ display: 'flex', flexDirection: 'column', pb: 0 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {/* Sticky top bar */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: (theme) => theme.zIndex.appBar - 1,
            bgcolor: 'background.default',
            pt: 0,
            pb: 2,
            mb: 0,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Clients
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {totalLabel}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <TextField
                placeholder="Search clients"
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ width: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'action.active', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => setShowClientForm(true)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Add Client
              </Button>
            </Box>
          </Box>

          {/* Collapsible filter row */}
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              onClick={() => setFiltersExpanded((v) => !v)}
              endIcon={filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ textTransform: 'none', color: 'text.secondary', mb: 1, p: 0, minWidth: 0 }}
            >
              Filters
            </Button>
            <Collapse in={filtersExpanded}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontWeight: 600 }}>
                    Status
                  </Typography>
                  {STATUS_FILTERS.map((f) => (
                    <Chip
                      key={f.value}
                      label={f.label}
                      size="small"
                      onClick={() => setFilterStatus(f.value)}
                      color={filterStatus === f.value ? 'primary' : 'default'}
                      variant={filterStatus === f.value ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontWeight: 600 }}>
                    Type
                  </Typography>
                  {TYPE_FILTERS.map((f) => (
                    <Chip
                      key={f.value}
                      label={f.label}
                      size="small"
                      onClick={() => setFilterClientType(f.value)}
                      color={filterClientType === f.value ? 'primary' : 'default'}
                      variant={filterClientType === f.value ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
                {filtersActive && (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={handleClearFilters}
                    sx={{ alignSelf: 'flex-start', cursor: 'pointer', fontWeight: 500 }}
                  >
                    Clear filters
                  </Link>
                )}
              </Box>
            </Collapse>
          </Box>
        </Box>

        {/* Bulk actions */}
        {rowSelectionModel.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              py: 1.5,
              px: 2,
              mb: 1,
              bgcolor: 'action.hover',
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {rowSelectionModel.length} client{rowSelectionModel.length !== 1 ? 's' : ''} selected
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AccountTreeIcon />}
                onClick={() => setAssignWorkflowOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Assign Workflow
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExport}
                sx={{ textTransform: 'none' }}
              >
                Export
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteConfirmOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Delete
              </Button>
            </Box>
          </Box>
        )}

        {emptyPortfolio ? (
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: 6,
              textAlign: 'center',
              borderColor: 'divider',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 2,
            }}
          >
            <PersonIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              No clients yet
            </Typography>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setShowClientForm(true)}
              sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
            >
              Add your first client
            </Button>
          </Paper>
        ) : noFilterResults ? (
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: 6,
              textAlign: 'center',
              borderColor: 'divider',
              flex: 1,
              mt: 2,
            }}
          >
            <PersonIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              No matching clients
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Try adjusting search or filters.
            </Typography>
            <Button variant="outlined" onClick={() => { setSearchQuery(''); handleClearFilters(); }} sx={{ textTransform: 'none' }}>
              Clear search & filters
            </Button>
          </Paper>
        ) : (
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              flex: 1,
              minHeight: 400,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderColor: 'divider',
              mt: 2,
            }}
          >
            <DataGrid
              rows={filteredRows}
              columns={columns}
              getRowId={(row) => row.id}
              loading={loading}
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={(newSelection) => setRowSelectionModel(newSelection)}
              checkboxSelection
              disableRowSelectionOnClick
              slots={{ loadingOverlay: LoadingSkeletonOverlay }}
              initialState={{
                pagination: { paginationModel: { pageSize: 50 } },
              }}
              pageSizeOptions={[25, 50, 100]}
              sx={{
                border: 'none',
                flex: 1,
                '& .MuiDataGrid-columnHeaders': {
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  bgcolor: 'background.paper',
                  borderBottom: 1,
                  borderColor: 'divider',
                },
                '& .MuiDataGrid-columnHeader': {
                  fontWeight: 700,
                },
                '& .MuiDataGrid-row:hover': {
                  bgcolor: 'action.hover',
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '0.875rem',
                  alignItems: 'center',
                  display: 'flex',
                },
              }}
            />
          </Paper>
        )}
      </Box>

      {showClientForm && (
        <ClientForm
          open={showClientForm}
          onClose={() => setShowClientForm(false)}
          onAddClient={() => {
            fetchData();
            setShowClientForm(false);
          }}
        />
      )}

      <Dialog open={assignWorkflowOpen} onClose={() => !assignWorkflowSending && setAssignWorkflowOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Workflow</DialogTitle>
        <DialogContent>
          {assignWorkflowError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {assignWorkflowError}
            </Alert>
          )}
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Workflow Template</InputLabel>
            <Select
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              label="Workflow Template"
            >
              <MenuItem value="">
                <em>Select a template</em>
              </MenuItem>
              {workflowTemplates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name || t.title || `Template ${t.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignWorkflowOpen(false)} disabled={assignWorkflowSending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignWorkflow}
            disabled={!selectedTemplateId || assignWorkflowSending}
            startIcon={assignWorkflowSending ? <CircularProgress size={16} /> : null}
          >
            {assignWorkflowSending ? 'Assigning…' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => !deleteSending && setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete selected clients?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete {rowSelectionModel.length} client{rowSelectionModel.length !== 1 ? 's' : ''}? This
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleteSending}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteSelected}
            disabled={deleteSending}
            startIcon={deleteSending ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleteSending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}

export default ClientList;
